/**
 * Sprint 3.4 — client-side admin export helpers (no server files; no secrets in payloads).
 */

const FORBIDDEN_EXPORT_KEYS = new Set([
  'password',
  'token',
  'secret',
  'authorization',
  'cookie',
  'session',
  'api_key',
  'apikey',
  'private_key',
]);

/**
 * @param {unknown} v
 * @returns {string}
 */
function serializeExportCell(v) {
  if (v == null || v === '') return '';
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') return String(v);
  if (t === 'bigint') return String(v);
  return '';
}

/**
 * Build plain-string rows for CSV from table rows + column keys (skips React nodes & nested objects).
 * @param {Array<Record<string, unknown>>} rows
 * @param {string[]} columnKeys
 * @returns {Array<Record<string, string>>}
 */
export function normalizeAdminExportRows(rows, columnKeys) {
  const keys = (columnKeys || []).filter((k) => k && !String(k).startsWith('__'));
  return (rows || []).map((row) => {
    const out = {};
    keys.forEach((k) => {
      if (FORBIDDEN_EXPORT_KEYS.has(String(k).toLowerCase())) {
        out[k] = '';
        return;
      }
      out[k] = serializeExportCell(row[k]);
    });
    return out;
  });
}

/**
 * @param {string} prefix e.g. plan-catalog
 * @param {string} ext csv | json
 */
export function buildExportFilename(prefix, ext) {
  const safe = String(prefix || 'export')
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  return `ydim-admin-${safe || 'export'}-${ts}.${ext}`;
}

function escapeCsvField(s) {
  const str = s == null ? '' : String(s);
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

/**
 * @param {Array<Record<string, string>>} rows
 * @param {string[]} headers column order
 * @returns {string}
 */
export function exportToCsv(rows, headers) {
  const cols = headers && headers.length ? headers : rows[0] ? Object.keys(rows[0]) : [];
  const lines = [];
  lines.push(cols.map((h) => escapeCsvField(h)).join(','));
  (rows || []).forEach((row) => {
    lines.push(cols.map((h) => escapeCsvField(row[h] ?? '')).join(','));
  });
  return lines.join('\r\n');
}

export function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType || 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * @param {unknown} obj must be a plain JSON-serializable snapshot (caller responsibility).
 */
export function exportToJsonFile(obj, filename) {
  const text = JSON.stringify(obj, null, 2);
  downloadBlob(text, filename, 'application/json;charset=utf-8');
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {string[]} columnKeys
 * @param {string} filePrefix
 */
export function exportAdminTableAsCsv(rows, columnKeys, filePrefix) {
  const keys = (columnKeys || []).filter((k) => k && !String(k).startsWith('__'));
  const norm = normalizeAdminExportRows(rows, keys);
  const csv = exportToCsv(norm, keys);
  downloadBlob(csv, buildExportFilename(filePrefix, 'csv'), 'text/csv;charset=utf-8');
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {string[]} columnKeys
 * @param {string} filePrefix
 */
export function exportAdminTableAsJson(rows, columnKeys, filePrefix) {
  const keys = (columnKeys || []).filter((k) => k && !String(k).startsWith('__'));
  const norm = normalizeAdminExportRows(rows, keys);
  exportToJsonFile(norm, buildExportFilename(filePrefix, 'json'));
}

export function copyTextToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
  return Promise.resolve();
}
